import { ArrowLeft, ChevronDown, Contact, ExternalLink, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { closable, messageRows, previewText, windowChats } from "@/lib/chats";
import { useChat, useChatMessages, useCloseChat, useMarkChatRead } from "@/lib/queries";
import type { ChatSummary } from "@/lib/types";
import { cn } from "@/lib/utils";
import {
  ChatIcon, CloseChatButton, Composer, listTime, MessageItem, threadSubtitle, ThreadView,
} from "@/routes/messages";

/**
 * Team Chat without leaving the page (platform ADR-186; the client asked for it on 2026-10-23): a
 * small chat window from the phone's rail, so a rep between calls or on one talks to their team or a
 * colleague where they are. The rail has one Team Chat button, with the unread count, however many
 * conversations the person is in (ACL-607): it opens the window on the list of them all, and one
 * press there opens a conversation. The conversations about contacts are in the window too, folded
 * at the end of its list.
 * Starting a new chat, and a conversation at full size, stay on the Team Chat page, which the window
 * links to.
 *
 * Whether the window is open, and on which conversation, is kept here for the whole console
 * (`openQuickChat`), so a message's toast opens the conversation in the window too.
 */

interface QuickChatState {
  open: boolean;
  /** The conversation open in the window; null for the list. */
  chatId: number | null;
  /** A thread open in that conversation (platform ADR-191); null for the conversation itself. */
  threadId: number | null;
}

let state: QuickChatState = { open: false, chatId: null, threadId: null };
const listeners = new Set<() => void>();

function set(next: QuickChatState) {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Open the window, on the list, one conversation, or one of its threads. */
export function openQuickChat(chatId: number | null = null, threadId: number | null = null) {
  set({ open: true, chatId, threadId: chatId === null ? null : threadId });
}

export function closeQuickChat() {
  if (state.open) set({ ...state, open: false });
}

export function useQuickChat(): QuickChatState {
  return React.useSyncExternalStore(subscribe, () => state);
}

/** The window's box: tall enough for a conversation and its composer, never past the window. */
export const QUICK_CHAT_CONTENT =
  "flex h-[34rem] w-96 max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0 max-h-[var(--radix-popover-content-available-height)]";

/** The window's body: the person's conversations, or the one open. */
export function QuickChat({ chats }: { chats: readonly ChatSummary[] }) {
  const { chatId, threadId } = useQuickChat();
  if (chatId === null) return <QuickList chats={chats} />;
  const summary = chats.find((c) => c.id === chatId) ?? null;
  if (threadId !== null) {
    return <QuickThreadOfChat key={`${chatId}:${threadId}`} chatId={chatId} rootId={threadId} summary={summary} />;
  }
  return <QuickThread key={chatId} chatId={chatId} summary={summary} />;
}

/** A thread in the window (platform ADR-191): back to its conversation, and the thread itself. */
function QuickThreadOfChat({ chatId, rootId, summary }: { chatId: number; rootId: number; summary: ChatSummary | null }) {
  return (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-2">
        <button type="button" aria-label="Back to the conversation" className={ICON_BUTTON} onClick={() => openQuickChat(chatId)}>
          <ArrowLeft className="size-4" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text">Thread</p>
          <p className="truncate text-2xs text-text-muted">{summary?.title ?? ""}</p>
        </div>
        <Tooltip content="Open in Team Chat" side="bottom">
          <Link
            to={`/messages/${chatId}?thread=${rootId}`}
            onClick={closeQuickChat}
            aria-label="Open in Team Chat"
            className={ICON_BUTTON}
          >
            <ExternalLink className="size-4" aria-hidden />
          </Link>
        </Tooltip>
        <button type="button" aria-label="Close" className={ICON_BUTTON} onClick={closeQuickChat}>
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col bg-surface-sunken">
        <ThreadView chatId={chatId} rootId={rootId} chat={summary} onOpenChat={(id) => openQuickChat(id)} />
      </div>
    </>
  );
}

const ICON_BUTTON =
  "flex size-7 shrink-0 items-center justify-center rounded-md text-text-muted hover:bg-surface-hover hover:text-text";

function UnreadBadge({ count }: { count: number }) {
  return (
    <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded bg-accent-9 px-1 text-2xs font-semibold text-white">
      <span className="sr-only">Unread: </span>
      {count > 99 ? "99+" : count}
    </span>
  );
}

function ChatListRow({ chat: c, me }: { chat: ChatSummary; me: number }) {
  const toast = useToast();
  const close = useCloseChat();
  const canClose = closable(c);
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => openQuickChat(c.id)}
        className="flex w-full items-start gap-2.5 border-b border-border px-3 py-2 text-left transition-colors hover:bg-surface-hover"
      >
        <ChatIcon chat={c} size="size-7" />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cn("min-w-0 flex-1 truncate text-sm text-text", c.unread > 0 && "font-semibold")}>
              {c.title}
            </span>
            <time
              className={cn(
                "shrink-0 text-2xs text-text-subtle",
                canClose && "group-focus-within:invisible group-hover:invisible",
              )}
            >
              {listTime(c.last_message?.created_at)}
            </time>
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <span className={cn("min-w-0 flex-1 truncate text-xs", c.unread > 0 ? "text-text" : "text-text-muted")}>
              {previewText(c.last_message, me)}
            </span>
            {c.unread > 0 && <UnreadBadge count={c.unread} />}
          </span>
        </span>
      </button>
      {canClose && (
        <CloseChatButton
          title={c.title}
          onClose={() =>
            close.mutate(c.id, {
              onError: (e) =>
                toast.error("Could not close the chat", e instanceof ApiError ? e.message : "Please try again."),
            })
          }
        />
      )}
    </div>
  );
}

/**
 * The person's chats: their teams and direct messages first, then, folded into one row at the end,
 * the conversations about contacts they handed off or took (platform ADR-189), which open from
 * there, from the Team Chat page and from the contact's record.
 */
function QuickList({ chats }: { chats: readonly ChatSummary[] }) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  const { people, contacts } = windowChats(chats);
  const [showContacts, setShowContacts] = React.useState(false);
  const contactsUnread = contacts.reduce((n, c) => n + c.unread, 0);
  return (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <h2 className="flex-1 text-sm font-semibold text-text">Team Chat</h2>
        <Link
          to="/messages"
          onClick={closeQuickChat}
          className="inline-flex items-center gap-1 text-xs text-accent-11 hover:underline"
        >
          <ExternalLink className="size-3.5" aria-hidden />
          Open Team Chat
        </Link>
        <button type="button" aria-label="Close" className={ICON_BUTTON} onClick={closeQuickChat}>
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle">
        {people.length === 0 && contacts.length === 0 ? (
          <p className="px-4 py-8 text-center text-xs text-text-muted">
            No conversations yet. A team you are in, or a message from Team Chat, starts one.
          </p>
        ) : (
          <>
            {people.length === 0 && (
              <p className="px-4 py-6 text-center text-xs text-text-muted">
                No team or direct messages yet. Start one in Team Chat.
              </p>
            )}
            {people.map((c) => (
              <ChatListRow key={c.id} chat={c} me={me} />
            ))}
            {contacts.length > 0 && (
              <>
                <button
                  type="button"
                  aria-expanded={showContacts}
                  onClick={() => setShowContacts((v) => !v)}
                  className="flex w-full items-center gap-2.5 border-b border-border bg-surface-sunken px-3 py-2 text-left text-xs text-text-muted transition-colors hover:bg-surface-hover"
                >
                  <Contact className="size-4 shrink-0" aria-hidden />
                  <span className="flex-1">
                    {showContacts ? "Hide contacts" : "Contacts"} · {contacts.length}
                  </span>
                  {contactsUnread > 0 && !showContacts && <UnreadBadge count={contactsUnread} />}
                  <ChevronDown className={cn("size-4 shrink-0 transition-transform", showContacts && "rotate-180")} aria-hidden />
                </button>
                {showContacts && contacts.map((c) => <ChatListRow key={c.id} chat={c} me={me} />)}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function QuickThread({ chatId, summary }: { chatId: number; summary: ChatSummary | null }) {
  const { user } = useAuth();
  const me = user?.id ?? 0;
  const messages = useChatMessages(chatId);
  const detail = useChat(chatId);
  const read = useMarkChatRead();
  const bottom = React.useRef<HTMLDivElement>(null);
  const newest = messages.data?.at(-1)?.id ?? null;

  // Read as it is opened, and as messages arrive while it is open: as the Team Chat page does.
  const readMutate = read.mutate;
  React.useEffect(() => {
    if (newest !== null && (summary?.unread ?? 0) > 0) readMutate({ chatId, messageId: newest });
  }, [chatId, newest, summary?.unread, readMutate]);
  React.useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [newest]);

  const rows = messageRows(messages.data ?? []);
  return (
    <>
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-2">
        <button type="button" aria-label="All conversations" className={ICON_BUTTON} onClick={() => openQuickChat(null)}>
          <ArrowLeft className="size-4" aria-hidden />
        </button>
        {summary && <ChatIcon chat={summary} size="size-7" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text">{summary?.title ?? "…"}</p>
          {summary && <p className="truncate text-2xs text-text-muted">{threadSubtitle(summary)}</p>}
        </div>
        <Tooltip content="Open in Team Chat" side="bottom">
          <Link to={`/messages/${chatId}`} onClick={closeQuickChat} aria-label="Open in Team Chat" className={ICON_BUTTON}>
            <ExternalLink className="size-4" aria-hidden />
          </Link>
        </Tooltip>
        <button type="button" aria-label="Close" className={ICON_BUTTON} onClick={closeQuickChat}>
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto bg-surface-sunken px-3 py-3 scrollbar-subtle">
        {messages.isPending ? (
          <div className="flex justify-center p-6" role="status">
            <Spinner className="size-5" />
          </div>
        ) : messages.error ? (
          <p className="py-8 text-center text-xs text-text-muted">Could not load the messages.</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-xs text-text-muted">No messages yet. Say hello.</p>
        ) : (
          rows.map(({ message, day, startsRun }) => (
            <React.Fragment key={message.id}>
              {day && (
                <div className="flex items-center gap-3 py-1.5 text-2xs text-text-subtle">
                  <span className="h-px flex-1 bg-border" />
                  {day}
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              <MessageItem
                message={message}
                startsRun={startsRun}
                mine={message.sender_id === me}
                people={detail.data?.members}
                chatKind={summary?.kind}
                onOpenChat={(id) => openQuickChat(id)}
                onOpenThread={(id) => openQuickChat(chatId, id)}
                readOnly={summary?.archived}
              />
            </React.Fragment>
          ))
        )}
        <div ref={bottom} />
      </div>
      {summary?.archived ? (
        <p className="shrink-0 border-t border-border bg-surface p-3 text-xs text-text-muted">
          This team was deleted. Its chat is kept to read, and takes no more messages.
        </p>
      ) : (
        <Composer
          chatId={chatId}
          placeholder={
            summary ? (summary.kind === "contact" ? `Message about ${summary.title}` : `Message ${summary.title}`) : "Message"
          }
        />
      )}
    </>
  );
}
